/* assets/cart-essentials.js
 *
 * Essentials for:
 * - cart drawer mounts
 * - product page mounts
 * - product page add-both button
 * - product page post-add reminder modal
 * - cart page mounts
 *
 * Product-page behavior:
 * - hide the whole essentials block if no essential exists
 * - show it only when an essential is found and resolved
 * - when normal Add to cart is clicked, allow the main product to add normally
 * - then show a reminder modal for the essential
 * - No thanks => close modal only
 * - Add with essential => add only the essential, then close modal
 *
 * UI behavior:
 * - drawer mounts still show the small Add button
 * - product-page mounts do NOT show the small Add button
 * - cart-page mounts refresh cart sections without requiring a manual page refresh
 *
 * Tracking behavior:
 * - normal Add to cart that triggers the reminder modal
 * - Add Essential from the reminder modal
 * - Add With Essentials from the product page
 * - cart-page small Add button
 */

const _essentialRecCache = new Map();
const _variantResolveCache = new Map();

const _essentialRecInFlight = new Map();
const _variantResolveInFlight = new Map();
let _cartFetchInFlight = null;

const _mountActionLocks = new WeakMap();

let _renderScheduled = false;
let _renderCooldownUntil = 0;
const RENDER_COOLDOWN_MS = 140;

let _cartCache = null;
const CART_CACHE_MS = 500;

const _optimisticInCart = new Map();
const OPTIMISTIC_MS = 1200;

let _essentialsReminderModal = null;
let _reminderModalBusy = false;
let _reminderRecentlyShownUntil = 0;
const REMINDER_COOLDOWN_MS = 1200;

// Set this to your actual client/store id if needed.
const CE_CLIENT_ID = 1;

/* --------------------------- optimistic cart --------------------------- */

function markOptimisticInCart(variantId) {
  _optimisticInCart.set(Number(variantId), Date.now() + OPTIMISTIC_MS);
}

function clearOptimisticInCart(variantId) {
  _optimisticInCart.delete(Number(variantId));
}

function isOptimisticInCart(variantId) {
  const key = Number(variantId);
  const exp = _optimisticInCart.get(key);
  if (!exp) return false;
  if (Date.now() > exp) {
    _optimisticInCart.delete(key);
    return false;
  }
  return true;
}

function clearExpiredOptimistic() {
  const now = Date.now();
  for (const [k, exp] of _optimisticInCart.entries()) {
    if (now > exp) _optimisticInCart.delete(k);
  }
}

function syncOptimisticWithCart(cart) {
  if (!cart || !Array.isArray(cart.items)) return;

  const cartVariantIds = new Set(
    cart.items.map((li) => Number(li.variant_id)).filter(Number.isFinite)
  );

  for (const variantId of _optimisticInCart.keys()) {
    if (!cartVariantIds.has(Number(variantId))) {
      _optimisticInCart.delete(Number(variantId));
    }
  }
}

/* ------------------------------ utils ------------------------------ */

function escapeHtml(str) {
  return String(str || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function normalizeHandle(input) {
  const s = String(input || "").trim();
  if (!s) return "";

  if (s.includes("/products/")) {
    return s.split("/products/")[1].split(/[?#]/)[0].toLowerCase();
  }

  return s.toLowerCase();
}

function formatMoneyFromCentsOrDollars(v) {
  const n = typeof v === "string" ? Number(v) : v;
  if (!Number.isFinite(n)) return "";
  const cents = n >= 100 ? n : Math.round(n * 100);
  return `$${(cents / 100).toFixed(2)}`;
}

function renderCheckPill() {
  return `
    <span class="ce-check"
      title="Already in cart"
      style="
        display:inline-flex;align-items:center;justify-content:center;
        width:28px;height:28px;border-radius:999px;
        background:rgba(34,197,94,.15);
        border:1px solid rgba(34,197,94,.45);
        color:rgb(22,163,74);
        font-weight:800;
        font-size:16px;
        line-height:1;
      "
    >✓</span>
  `;
}

function renderAddPill(qty) {
  const label = qty > 1 ? `Add x${qty}` : "Add";
  return `
    <button class="ce-add" type="button"
      style="
        padding:6px 10px;border-radius:999px;
        border:1px solid rgba(0,0,0,.15);
        background:#fff;cursor:pointer;
        font-size:12px;
      "
    >${label}</button>
  `;
}

function setMountBusy(el, isBusy) {
  _mountActionLocks.set(el, !!isBusy);
  el.dataset.ceBusy = isBusy ? "1" : "0";

  const btn = el.querySelector(".ce-add");
  if (btn) btn.disabled = !!isBusy;
}

function isMountBusy(el) {
  return _mountActionLocks.get(el) === true || el.dataset.ceBusy === "1";
}

function isProductPage() {
  return !!document.querySelector("[data-cart-essentials-product]");
}

function isCartPage() {
  return (
    window.location.pathname === "/cart" ||
    !!document.querySelector("#main-cart-items") ||
    !!document.querySelector("cart-items")
  );
}

function getCurrentProductMount() {
  return document.querySelector("[data-cart-essentials-product]");
}

function isReminderCooldownActive() {
  return Date.now() < _reminderRecentlyShownUntil;
}

function touchReminderCooldown() {
  _reminderRecentlyShownUntil = Date.now() + REMINDER_COOLDOWN_MS;
}

function isProductMount(el) {
  return !!el?.hasAttribute?.("data-cart-essentials-product");
}

/* ------------------------------ tracking ------------------------------ */

function buildTrackingUuid(action, productHandle, essentialHandle) {
  return [
    "ce",
    action || "unknown",
    normalizeHandle(productHandle || "no-product"),
    normalizeHandle(essentialHandle || "no-essential"),
    Date.now(),
    Math.random().toString(36).slice(2, 8),
  ].join(":");
}

async function trackFeatureClick({
  action,
  clientId = CE_CLIENT_ID,
  productHandle = "",
  essentialHandle = "",
}) {
  try {
    const uuid = buildTrackingUuid(action, productHandle, essentialHandle);

    const r = await fetch("/apps/pointofcell/save-acceptance", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        uuid,
        client_id: Number(clientId),
        accepted: true,
      }),
    });

    const text = await r.text();
    console.log("save-acceptance", action, r.status, text);
  } catch (e) {
    console.warn("save-acceptance failed:", action, e);
  }
}

/* --------------------- product block show / hide --------------------- */

function getProductEssentialsRoot(el) {
  if (!el) return null;
  return el.closest("[data-cart-essentials-root]");
}

function getProductAddBothButton(el) {
  const root = getProductEssentialsRoot(el);
  if (root) {
    return root.querySelector("[data-cart-essentials-add-both]");
  }
  return document.querySelector("[data-cart-essentials-add-both]");
}

function hideProductEssentialsUI(el) {
  const root = getProductEssentialsRoot(el);
  const btn = getProductAddBothButton(el);

  if (root) {
    root.hidden = true;
    root.style.display = "none";
  }

  if (btn) {
    btn.hidden = true;
    btn.style.display = "none";
    btn.disabled = true;
  }

  if (el) {
    el.innerHTML = "";
    delete el.dataset.ceResolvedVariantId;
    delete el.dataset.ceRecQty;
    delete el.dataset.ceUiSig;
    delete el.dataset.ceEssentialHandle;
  }
}

function showProductEssentialsUI(el) {
  const root = getProductEssentialsRoot(el);
  const btn = getProductAddBothButton(el);

  if (root) {
    root.hidden = false;
    root.style.display = "";
  }

  if (btn) {
    btn.hidden = false;
    btn.style.display = "";
    btn.disabled = false;
  }
}

/* ------------------------------ cart cache ------------------------------ */

function bumpCartCacheOptimistically(variantId, quantity = 1) {
  if (!_cartCache?.data) return;

  const cart = _cartCache.data;
  const qty = Number(quantity) || 1;
  const id = Number(variantId);

  cart.item_count = Number(cart.item_count || 0) + qty;

  if (!Array.isArray(cart.items)) cart.items = [];
  const existing = cart.items.find((li) => Number(li.variant_id) === id);

  if (existing) {
    existing.quantity = Number(existing.quantity || 0) + qty;
  } else {
    cart.items.push({
      variant_id: id,
      id,
      quantity: qty,
    });
  }

  _cartCache.ts = Date.now();
}

function replaceCartCache(cart) {
  if (!cart) return;
  syncOptimisticWithCart(cart);
  _cartCache = { data: cart, ts: Date.now() };
}

function invalidateCartCache() {
  _cartCache = null;
  _cartFetchInFlight = null;
}

function updateHeaderBubbleCount(itemCount) {
  const count = Number(itemCount);
  if (!Number.isFinite(count)) return;

  const existingNodes = [
    ...document.querySelectorAll(".cart-bubble__text-count"),
    ...document.querySelectorAll("[data-testid='cart-bubble-text']"),
    ...document.querySelectorAll("[data-cart-count]"),
    ...document.querySelectorAll(".cart-count-bubble span, .cart-count-bubble"),
  ];

  if (existingNodes.length) {
    for (const el of existingNodes) {
      if (el.classList.contains("cart-bubble__text-count")) {
        el.textContent = count > 0 ? String(count) : "";
      } else if (el.matches("[data-cart-count]")) {
        el.setAttribute("data-cart-count", String(count));
        el.textContent = count > 0 ? String(count) : "";
      } else {
        const t = (el.textContent || "").trim();
        if (/^\d*$/.test(t)) {
          el.textContent = count > 0 ? String(count) : "";
        }
      }
    }
    return;
  }

  if (count <= 0) return;

  const cartIcon =
    document.querySelector('a[href="/cart"]') ||
    document.querySelector('a[href*="/cart"]') ||
    document.querySelector(".header__icon--cart") ||
    document.querySelector(".header__icons a");

  if (!cartIcon) return;

  let bubble = cartIcon.querySelector(".cart-count-bubble");
  if (!bubble) {
    bubble = document.createElement("div");
    bubble.className = "cart-count-bubble";
    bubble.style.cssText = `
      position:absolute;
      top:-6px;
      right:-8px;
      min-width:18px;
      height:18px;
      padding:0 4px;
      border-radius:999px;
      background:#111;
      color:#fff;
      display:flex;
      align-items:center;
      justify-content:center;
      font-size:11px;
      line-height:1;
      font-weight:600;
      pointer-events:none;
    `;

    const span = document.createElement("span");
    bubble.appendChild(span);

    const computed = window.getComputedStyle(cartIcon);
    if (computed.position === "static") {
      cartIcon.style.position = "relative";
    }

    cartIcon.appendChild(bubble);
  }

  const span = bubble.querySelector("span") || bubble;
  span.textContent = String(count);
}

/* ------------------------------ render scheduling ------------------------------ */

function scheduleRenderAllEssentialsMounts() {
  const now = Date.now();
  if (_renderScheduled) return;

  const delay = Math.max(0, _renderCooldownUntil - now);

  _renderScheduled = true;
  window.setTimeout(async () => {
    _renderScheduled = false;
    _renderCooldownUntil = Date.now() + RENDER_COOLDOWN_MS;

    try {
      await renderAllEssentialsMounts();
    } catch (e) {
      console.warn("Essentials render pass failed:", e);
    }
  }, delay);
}

async function forceFreshEssentialsRender() {
  try {
    await getCartCached(true);
  } catch (e) {
    console.warn("Fresh cart fetch failed:", e);
  }
  scheduleRenderAllEssentialsMounts();
}

/* ------------------------------ cart api ------------------------------ */

async function getCartCached(force = false) {
  const now = Date.now();

  if (!force && _cartCache && now - _cartCache.ts < CART_CACHE_MS) {
    return _cartCache.data;
  }

  if (!force && _cartFetchInFlight) {
    return _cartFetchInFlight;
  }

  const p = fetch("/cart.js", {
    cache: "no-store",
    headers: { Accept: "application/json" },
  })
    .then(async (r) => {
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();
      replaceCartCache(data);
      return data;
    })
    .finally(() => {
      _cartFetchInFlight = null;
    });

  _cartFetchInFlight = p;
  return p;
}

async function addVariantToCart(variantId, quantity = 1) {
  const r = await fetch("/cart/add.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      items: [{ id: Number(variantId), quantity: Number(quantity) }],
    }),
  });

  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

async function addItemsToCart(items) {
  const cleanItems = (items || [])
    .map((x) => ({
      id: Number(x.id),
      quantity: Number(x.quantity || 1),
    }))
    .filter((x) => Number.isFinite(x.id) && x.quantity > 0);

  if (!cleanItems.length) {
    throw new Error("No valid items to add.");
  }

  const r = await fetch("/cart/add.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ items: cleanItems }),
  });

  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

/* ------------------------------ refresh helpers ------------------------------ */

async function refreshDrawerViaSectionId() {
  const comp = document.querySelector("cart-items-component");
  const sectionId = comp?.dataset?.sectionId;
  if (!sectionId) return false;

  const url = `/?section_id=${encodeURIComponent(sectionId)}`;
  const r = await fetch(url, {
    headers: { Accept: "text/html" },
    cache: "no-store",
  });
  if (!r.ok) return false;

  const html = await r.text();
  const doc = new DOMParser().parseFromString(html, "text/html");
  const fresh = doc.querySelector("cart-items-component");
  if (!fresh) return false;

  comp.innerHTML = fresh.innerHTML;
  return true;
}

async function refreshDrawerViaSectionsParam() {
  const sectionIds = ["cart-drawer", "cart-icon-bubble"];
  const sectionsParam = sectionIds.map(encodeURIComponent).join(",");
  const url = `${window.location.pathname}?sections=${sectionsParam}`;

  const r = await fetch(url, { cache: "no-store" });
  if (!r.ok) return false;

  const sections = await r.json();

  for (const id of sectionIds) {
    const payload = sections[id];
    const html =
      typeof payload === "string"
        ? payload
        : (payload && (payload.html || payload.section || payload.content)) || "";

    if (!html) continue;

    const host = document.querySelector(`#shopify-section-${CSS.escape(id)}`);
    if (host) host.innerHTML = html;
  }

  return true;
}

async function refreshCartPageSections() {
  const r = await fetch("/cart", {
    cache: "no-store",
    headers: { Accept: "text/html" },
  });

  if (!r.ok) return false;

  const html = await r.text();
  const doc = new DOMParser().parseFromString(html, "text/html");

  let updated = false;

  const currentItems =
    document.querySelector("#main-cart-items") ||
    document.querySelector("cart-items #main-cart-items") ||
    document.querySelector(".cart__items");

  const freshItems =
    doc.querySelector("#main-cart-items") ||
    doc.querySelector("cart-items #main-cart-items") ||
    doc.querySelector(".cart__items");

  if (currentItems && freshItems) {
    currentItems.innerHTML = freshItems.innerHTML;
    updated = true;
  }

  const currentFooter =
    document.querySelector("#main-cart-footer") ||
    document.querySelector(".cart__footer");

  const freshFooter =
    doc.querySelector("#main-cart-footer") ||
    doc.querySelector(".cart__footer");

  if (currentFooter && freshFooter) {
    currentFooter.innerHTML = freshFooter.innerHTML;
    updated = true;
  }

  const currentLiveRegion = document.querySelector("#cart-live-region-text");
  const freshLiveRegion = doc.querySelector("#cart-live-region-text");
  if (currentLiveRegion && freshLiveRegion) {
    currentLiveRegion.innerHTML = freshLiveRegion.innerHTML;
  }

  const currentBubble =
    document.querySelector("#shopify-section-cart-icon-bubble") ||
    document.querySelector("cart-icon-bubble") ||
    document.querySelector(".shopify-section .header__icon--cart");

  const freshBubble =
    doc.querySelector("#shopify-section-cart-icon-bubble") ||
    doc.querySelector("cart-icon-bubble") ||
    doc.querySelector(".shopify-section .header__icon--cart");

  if (currentBubble && freshBubble) {
    currentBubble.innerHTML = freshBubble.innerHTML;
  }

  return updated;
}

async function refreshCartDrawerAndBubble() {
  try {
    let ok = false;

    if (isCartPage()) {
      ok = await refreshCartPageSections();
    } else {
      ok = await refreshDrawerViaSectionId();
      if (!ok) ok = await refreshDrawerViaSectionsParam();
    }

    if (!ok) throw new Error("No compatible cart refresh method found.");

    const cart = await getCartCached(true);
    updateHeaderBubbleCount(cart.item_count);

    document.dispatchEvent(new CustomEvent("cart:updated"));
    scheduleRenderAllEssentialsMounts();
  } catch (e) {
    console.warn("Failed to refresh cart UI; falling back to reload.", e);
    window.location.reload();
  }
}

/* ------------------------------ product lookup ------------------------------ */

async function resolveVariantFromInput(input) {
  const rawKey = String(input || "").trim();
  if (!rawKey) return null;

  const handle = normalizeHandle(rawKey);
  if (!handle) return null;

  if (_variantResolveCache.has(handle)) {
    return _variantResolveCache.get(handle);
  }

  if (_variantResolveInFlight.has(handle)) {
    return _variantResolveInFlight.get(handle);
  }

  const task = (async () => {
    try {
      const r = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: "application/json" },
        cache: "no-store",
      });

      if (!r.ok) {
        _variantResolveCache.set(handle, null);
        return null;
      }

      const p = await r.json();
      const variantId = p?.variants?.[0]?.id ? Number(p.variants[0].id) : null;

      const out = variantId ? { variantId, handle: p.handle || handle, product: p } : null;

      _variantResolveCache.set(handle, out);
      return out;
    } catch (e) {
      console.warn("Variant resolution failed for handle:", handle, e);
      _variantResolveCache.set(handle, null);
      return null;
    }
  })().finally(() => {
    _variantResolveInFlight.delete(handle);
  });

  _variantResolveInFlight.set(handle, task);
  return task;
}

async function fetchEssentialForCartItem(itemInput) {
  const key = normalizeHandle(itemInput);
  if (!key) return null;

  if (_essentialRecCache.has(key)) {
    return _essentialRecCache.get(key);
  }

  if (_essentialRecInFlight.has(key)) {
    return _essentialRecInFlight.get(key);
  }

  const task = (async () => {
    let out = null;

    try {
      const res = await fetch(`/apps/pointofcell?upcs=${encodeURIComponent(key)}`, {
        cache: "no-store",
        headers: { Accept: "application/json" },
      });

      if (!res.ok) {
        _essentialRecCache.set(key, null);
        return null;
      }

      const data = await res.json();

      if (Array.isArray(data) && data.length) {
        const row = data[0] || {};
        const input = normalizeHandle(row.fulfilling_upc || row.handle || row.input || "");
        const qty =
          Number(row.fulfilling_quantity || row.fulfilling_qty || row.qty || 1) || 1;
        out = input ? { input, qty } : null;
      } else if (data && typeof data === "object") {
        if (Array.isArray(data.essentials) && data.essentials.length) {
          const first = data.essentials[0];

          if (typeof first === "string") {
            out = { input: normalizeHandle(first), qty: 1 };
          } else {
            out = {
              input: normalizeHandle(
                first.fulfilling_upc || first.handle || first.input || ""
              ),
              qty:
                Number(first.fulfilling_quantity || first.fulfilling_qty || first.qty || 1) ||
                1,
            };
          }

          if (!out.input) out = null;
        } else if (data.fulfilling_upc || data.handle || data.input) {
          out = {
            input: normalizeHandle(data.fulfilling_upc || data.handle || data.input || ""),
            qty: Number(data.fulfilling_quantity || data.fulfilling_qty || data.qty || 1) || 1,
          };

          if (!out.input) out = null;
        }
      }
    } catch (e) {
      console.warn("Essentials proxy failed:", e);
      out = null;
    }

    if (!out?.input) out = null;
    if (out && (!out.qty || out.qty < 1)) out.qty = 1;

    _essentialRecCache.set(key, out);
    return out;
  })().finally(() => {
    _essentialRecInFlight.delete(key);
  });

  _essentialRecInFlight.set(key, task);
  return task;
}

/* ------------------------------ modal helpers ------------------------------ */

function ensureReminderModal() {
  if (_essentialsReminderModal && document.body.contains(_essentialsReminderModal)) {
    return _essentialsReminderModal;
  }

  const modal = document.createElement("div");
  modal.setAttribute("data-ce-reminder-modal", "");
  modal.hidden = true;
  modal.style.cssText = `
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: none;
  `;

  modal.innerHTML = `
    <div data-ce-reminder-backdrop style="
      position:absolute;
      inset:0;
      background:rgba(0,0,0,.45);
    "></div>

    <div role="dialog" aria-modal="true" aria-labelledby="ce-reminder-title" style="
      position:absolute;
      top:50%;
      left:50%;
      transform:translate(-50%, -50%);
      width:min(92vw, 460px);
      background:#fff;
      border-radius:16px;
      box-shadow:0 20px 60px rgba(0,0,0,.22);
      padding:18px;
      font-family:inherit;
    ">
      <div style="
        display:flex;
        align-items:start;
        justify-content:space-between;
        gap:12px;
        margin-bottom:12px;
      ">
        <div>
          <div id="ce-reminder-title" style="font-size:18px;font-weight:700;">
            Add the Required Essential?
          </div>
          <div style="font-size:13px;opacity:.75;margin-top:4px;">
            An additional item is needed for this product to operate. Complete your purchase now?
          </div>
        </div>

        <button type="button" data-ce-reminder-close aria-label="Close" style="
          border:0;
          background:transparent;
          font-size:22px;
          line-height:1;
          cursor:pointer;
          padding:0;
        ">×</button>
      </div>

      <div data-ce-reminder-body></div>

      <div style="
        display:flex;
        gap:10px;
        justify-content:flex-end;
        margin-top:16px;
        flex-wrap:wrap;
      ">
        <button type="button" data-ce-reminder-decline style="
          padding:10px 14px;
          border-radius:999px;
          border:1px solid rgba(0,0,0,.15);
          background:#fff;
          cursor:pointer;
          font-size:14px;
        ">No thanks</button>

        <button type="button" data-ce-reminder-accept style="
          padding:10px 14px;
          border-radius:999px;
          border:0;
          background:#111;
          color:#fff;
          cursor:pointer;
          font-size:14px;
          font-weight:600;
        ">Add Essential</button>
      </div>
    </div>
  `;

  document.body.appendChild(modal);
  _essentialsReminderModal = modal;
  return modal;
}

function closeReminderModal() {
  const modal = ensureReminderModal();
  modal.hidden = true;
  modal.style.display = "none";

  const acceptBtn = modal.querySelector("[data-ce-reminder-accept]");
  const declineBtn = modal.querySelector("[data-ce-reminder-decline]");
  const closeBtn = modal.querySelector("[data-ce-reminder-close]");

  if (acceptBtn) {
    acceptBtn.disabled = false;
    acceptBtn.textContent = "Add Essential";
  }
  if (declineBtn) {
    declineBtn.disabled = false;
    declineBtn.textContent = "No thanks";
  }
  if (closeBtn) closeBtn.disabled = false;

  _reminderModalBusy = false;
}

function setReminderModalBusy(busy, mode = "") {
  _reminderModalBusy = !!busy;

  const modal = ensureReminderModal();
  const acceptBtn = modal.querySelector("[data-ce-reminder-accept]");
  const declineBtn = modal.querySelector("[data-ce-reminder-decline]");
  const closeBtn = modal.querySelector("[data-ce-reminder-close]");

  if (acceptBtn) {
    acceptBtn.disabled = !!busy;
    acceptBtn.textContent = busy && mode === "accept" ? "Adding…" : "Add Essential";
  }

  if (declineBtn) {
    declineBtn.disabled = !!busy;
    declineBtn.textContent = busy && mode === "decline" ? "Closing…" : "No thanks";
  }

  if (closeBtn) closeBtn.disabled = !!busy;
}

function openReminderModal(model, handlers) {
  const modal = ensureReminderModal();
  const body = modal.querySelector("[data-ce-reminder-body]");
  const acceptBtn = modal.querySelector("[data-ce-reminder-accept]");
  const declineBtn = modal.querySelector("[data-ce-reminder-decline]");
  const closeBtn = modal.querySelector("[data-ce-reminder-close]");
  const backdrop = modal.querySelector("[data-ce-reminder-backdrop]");

  if (!body || !acceptBtn || !declineBtn || !closeBtn || !backdrop) return;

  body.innerHTML = `
    <div style="
      display:flex;
      align-items:center;
      gap:12px;
      padding:12px;
      border:1px solid rgba(0,0,0,.08);
      border-radius:12px;
      background:#f8f8f8;
    ">
      <div style="
        width:52px;
        height:52px;
        border-radius:10px;
        overflow:hidden;
        flex:0 0 52px;
        background:#fff;
        border:1px solid rgba(0,0,0,.08);
        display:flex;
        align-items:center;
        justify-content:center;
      ">
        ${
          model.img
            ? `<img src="${escapeHtml(model.img)}" alt="" style="width:100%;height:100%;object-fit:cover;" />`
            : ``
        }
      </div>

      <div style="min-width:0;flex:1;">
        <div style="font-size:14px;font-weight:600;line-height:1.3;">
          ${escapeHtml(model.name || "")}
        </div>
        <div style="font-size:13px;opacity:.75;margin-top:4px;">
          ${escapeHtml(model.price || "")}${model.qty > 1 ? ` • Qty ${model.qty}` : ""}
        </div>
      </div>
    </div>
  `;

  modal.hidden = false;
  modal.style.display = "block";
  touchReminderCooldown();

  const onClose = () => {
    if (_reminderModalBusy) return;
    closeReminderModal();
  };

  const onDecline = async () => {
    if (_reminderModalBusy) return;
    setReminderModalBusy(true, "decline");
    try {
      await handlers.onDecline();
      closeReminderModal();
    } catch (e) {
      console.error(e);
      setReminderModalBusy(false);
    }
  };

  const onAccept = async () => {
    if (_reminderModalBusy) return;
    setReminderModalBusy(true, "accept");
    try {
      await handlers.onAccept();
      closeReminderModal();
    } catch (e) {
      console.error(e);
      setReminderModalBusy(false);
    }
  };

  backdrop.onclick = onClose;
  closeBtn.onclick = onClose;
  declineBtn.onclick = onDecline;
  acceptBtn.onclick = onAccept;
}

/* ------------------------------ mount rendering ------------------------------ */

function ensureMountSkeleton(el) {
  let card = el.querySelector(".ce-card");
  if (card) return card;

  el.innerHTML = `
    <div class="ce-card" style="
      padding:12px;
      border-radius:12px;
      background:#f7f7f7;
    ">
      <div class="ce-title" style="font-weight:600; font-size:13px; margin-bottom:8px;"></div>
      <div style="display:flex; align-items:center; gap:10px;">
        <div class="ce-img-wrap"></div>
        <div style="flex:1; min-width:0;">
          <div class="ce-name" style="font-size:13px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;"></div>
          <div class="ce-price" style="font-size:12px; opacity:.75;"></div>
        </div>
        <div class="ce-cta"></div>
      </div>
      <div class="ce-msg" style="margin-top:8px; font-size:12px; opacity:.75;"></div>
    </div>
  `;

  return el.querySelector(".ce-card");
}

function clearMountIfNoRecommendation(el) {
  if (!el) return;
  if (isMountBusy(el)) return;
  el.innerHTML = "";
  delete el.dataset.ceResolvedVariantId;
  delete el.dataset.ceRecQty;
  delete el.dataset.ceUiSig;
  delete el.dataset.ceEssentialHandle;
}

function bindAddButton(el, variantId, qty) {
  const btn = el.querySelector(".ce-add");
  const msg = el.querySelector(".ce-msg");
  const cta = el.querySelector(".ce-cta");

  if (!btn || !cta) return;

  const sig = `${variantId}:${qty}`;
  if (btn.dataset.ceBound === sig) return;
  btn.dataset.ceBound = sig;

  btn.addEventListener("click", async () => {
    if (isMountBusy(el)) return;

    setMountBusy(el, true);
    if (msg) msg.textContent = "Adding…";

    try {
      await addVariantToCart(variantId, qty);

      trackFeatureClick({
        action: "cart_add_essential",
        clientId: CE_CLIENT_ID,
        productHandle: el.dataset.input || "",
        essentialHandle: el.dataset.ceEssentialHandle || "",
      });

      markOptimisticInCart(variantId);
      bumpCartCacheOptimistically(variantId, qty);

      cta.innerHTML = renderCheckPill();
      if (msg) msg.textContent = "Added!";

      if (_cartCache?.data?.item_count != null) {
        updateHeaderBubbleCount(_cartCache.data.item_count);
      }

      invalidateCartCache();

      refreshCartDrawerAndBubble().catch((e) => {
        console.warn("Background cart refresh failed:", e);
      });
    } catch (e) {
      console.error(e);
      clearOptimisticInCart(variantId);
      cta.innerHTML = renderAddPill(qty);
      if (msg) msg.textContent = "Couldn’t add.";
      bindAddButton(el, variantId, qty);
    } finally {
      setMountBusy(el, false);
    }
  });
}

function patchMountUI(el, model) {
  const card = ensureMountSkeleton(el);
  if (!card) return;

  const titleEl = el.querySelector(".ce-title");
  const imgWrap = el.querySelector(".ce-img-wrap");
  const nameEl = el.querySelector(".ce-name");
  const priceEl = el.querySelector(".ce-price");
  const cta = el.querySelector(".ce-cta");
  const msg = el.querySelector(".ce-msg");
  const productMount = isProductMount(el);

  if (titleEl) {
    titleEl.textContent = model.inCart
      ? "Your cart already contains requirements for operation!"
      : "This item requires essential components to operate. Complete your purchase with the items below.";
  }

  if (imgWrap) {
    imgWrap.innerHTML = model.img
      ? `<img src="${escapeHtml(model.img)}" alt="" style="width:38px;height:38px;object-fit:cover;border-radius:8px;border:1px solid rgba(0,0,0,.08);" />`
      : "";
  }

  if (nameEl) nameEl.textContent = model.name;
  if (priceEl) priceEl.textContent = model.price || "";

  const currentSig = el.dataset.ceUiSig || "";
  const nextSig = `${model.variantId}|${model.qty}|${model.inCart ? "1" : "0"}|${model.name}|${model.price}|${model.img}`;

  if (currentSig !== nextSig && cta && !isMountBusy(el)) {
    if (productMount) {
      cta.innerHTML = model.inCart ? renderCheckPill() : "";
    } else {
      cta.innerHTML = model.inCart ? renderCheckPill() : renderAddPill(model.qty);
    }
    el.dataset.ceUiSig = nextSig;
  }

  el.dataset.ceResolvedVariantId = String(model.variantId);
  el.dataset.ceRecQty = String(model.qty);

  if (!productMount && !model.inCart && !isMountBusy(el)) {
    bindAddButton(el, model.variantId, model.qty);
  }

  if (msg && !isMountBusy(el) && msg.textContent === "Adding…") {
    msg.textContent = "";
  }
}

/* ------------------------------ main product helpers ------------------------------ */

function getSelectedMainProductVariantId() {
  const checkedVariantInput =
    document.querySelector('form[action*="/cart/add"] input[name="id"]:checked') ||
    document.querySelector('input[name="id"]:checked');

  if (checkedVariantInput?.value) {
    const id = Number(checkedVariantInput.value);
    if (Number.isFinite(id)) return id;
  }

  const hiddenVariantInput =
    document.querySelector('form[action*="/cart/add"] input[name="id"]') ||
    document.querySelector('input[name="id"]');

  if (hiddenVariantInput?.value) {
    const id = Number(hiddenVariantInput.value);
    if (Number.isFinite(id)) return id;
  }

  return null;
}

function getSelectedMainProductQuantity() {
  const qtyInput =
    document.querySelector('form[action*="/cart/add"] input[name="quantity"]') ||
    document.querySelector('input[name="quantity"]');

  const qty = Number(qtyInput?.value || 1);
  return Number.isFinite(qty) && qty > 0 ? qty : 1;
}

/* ------------------------------ reminder modal flow ------------------------------ */

async function addEssentialOnly(essentialVariantId, essentialQty) {
  await addVariantToCart(essentialVariantId, essentialQty);

  const mount = getCurrentProductMount();
  trackFeatureClick({
    action: "modal_add_essential",
    clientId: CE_CLIENT_ID,
    productHandle: mount?.dataset?.productHandle || "",
    essentialHandle: mount?.dataset?.ceEssentialHandle || "",
  });

  markOptimisticInCart(essentialVariantId);
  bumpCartCacheOptimistically(essentialVariantId, essentialQty);

  if (_cartCache?.data?.item_count != null) {
    updateHeaderBubbleCount(_cartCache.data.item_count);
  }

  invalidateCartCache();

  refreshCartDrawerAndBubble().catch((e) => {
    console.warn("Background cart refresh failed:", e);
  });
}

async function maybeShowEssentialsReminderAfterAdd() {
  if (!isProductPage()) return false;
  if (_reminderModalBusy) return false;
  if (isReminderCooldownActive()) return false;

  const mount = getCurrentProductMount();
  if (!mount) return false;

  const essentialVariantId = Number(mount.dataset.ceResolvedVariantId || 0);
  const essentialQty = Number(mount.dataset.ceRecQty || 1);

  if (!Number.isFinite(essentialVariantId) || essentialVariantId <= 0) return false;

  const itemInputRaw = mount.dataset.productHandle || "";
  const itemInput = normalizeHandle(itemInputRaw);
  if (!itemInput) return false;

  const rec = await fetchEssentialForCartItem(itemInput);
  if (!rec?.input) return false;

  const resolved = await resolveVariantFromInput(rec.input);
  if (!resolved?.variantId || !resolved?.product) return false;

  const currentCart = await getCartCached(true);
  const alreadyInCart =
    isOptimisticInCart(resolved.variantId) ||
    (currentCart.items || []).some((li) => Number(li.variant_id) === Number(resolved.variantId));

  if (alreadyInCart) return false;

  const p = resolved.product;
  const img = p.featured_image || (p.images && p.images[0]) || "";
  const price = p?.variants?.[0]?.price ?? null;


  openReminderModal(
    {
      name: String(p.title || resolved.handle || ""),
      price: price !== null ? formatMoneyFromCentsOrDollars(price) : "",
      img: String(img || ""),
      qty: Number(rec.qty || 1),
    },
    {
      onDecline: async () => {},
      onAccept: async () => {
        await addEssentialOnly(essentialVariantId, essentialQty);
      },
    }
  );

  return true;
}

function bindProductFormReminderIntercept() {
  const forms = Array.from(document.querySelectorAll('form[action*="/cart/add"]'));
  if (!forms.length) return;

  for (const form of forms) {
    if (form.dataset.ceReminderBound === "1") continue;
    form.dataset.ceReminderBound = "1";

    const addBtn =
      form.querySelector('button[name="add"]') ||
      form.querySelector('.product-form__submit[name="add"]') ||
      form.querySelector('button[type="submit"][name="add"]') ||
      form.querySelector('button[type="submit"]');

    if (!addBtn) continue;

    addBtn.addEventListener("click", () => {
      if (!isProductPage()) return;
      if (_reminderModalBusy) return;
      if (addBtn.disabled) return;

      window.setTimeout(async () => {
        try {
          await maybeShowEssentialsReminderAfterAdd();
        } catch (err) {
          console.warn("Essentials reminder flow failed:", err);
        }
      }, 550);
    });
  }
}

/* ------------------------------ add both button ------------------------------ */

function bindAddBothButton(rootEl) {
  const scope = rootEl || document;
  const btn =
    scope.querySelector?.("[data-cart-essentials-add-both]") ||
    document.querySelector("[data-cart-essentials-add-both]");
  const mount =
    scope.querySelector?.("[data-cart-essentials-product]") ||
    document.querySelector("[data-cart-essentials-product]");

  if (!btn || !mount) return;
  if (btn.dataset.ceBound === "1") return;
  btn.dataset.ceBound = "1";

  btn.addEventListener("click", async () => {
    if (btn.disabled) return;

    const mainVariantId = getSelectedMainProductVariantId();
    const mainQty = getSelectedMainProductQuantity();
    const essentialVariantId = Number(mount.dataset.ceResolvedVariantId || 0);
    const essentialQty = Number(mount.dataset.ceRecQty || 1);

    if (!mainVariantId) {
      alert("Could not find the selected product variant.");
      return;
    }

    if (!Number.isFinite(essentialVariantId) || essentialVariantId <= 0) {
      alert("Essential item is not available yet.");
      return;
    }

    btn.disabled = true;
    const originalText = btn.textContent;
    btn.textContent = "Adding both…";

    try {
      await addItemsToCart([
        { id: mainVariantId, quantity: mainQty },
        { id: essentialVariantId, quantity: essentialQty },
      ]);

      trackFeatureClick({
        action: "add_with_essentials",
        clientId: CE_CLIENT_ID,
        productHandle: mount.dataset.productHandle || "",
        essentialHandle: mount.dataset.ceEssentialHandle || "",
      });

      markOptimisticInCart(essentialVariantId);
      bumpCartCacheOptimistically(mainVariantId, mainQty);
      bumpCartCacheOptimistically(essentialVariantId, essentialQty);

      btn.textContent = "Added both!";
      touchReminderCooldown();
      invalidateCartCache();

      refreshCartDrawerAndBubble().catch((e) => {
        console.warn("Background cart refresh failed:", e);
      });

      window.setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 1200);
    } catch (e) {
      console.error(e);
      btn.textContent = "Couldn’t add both";
      window.setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 1500);
    }
  });
}

/* ------------------------------ mount renderers ------------------------------ */

async function renderDrawerMount(el, cart) {
  if (!el || !el.isConnected) return;
  if (el.dataset.ceRendering === "1") return;
  if (isMountBusy(el)) return;

  el.dataset.ceRendering = "1";

  try {
    const itemInputRaw = el.dataset.input || "";
    const itemInput = normalizeHandle(itemInputRaw);
    const title = el.dataset.title || "this item";

    const hasEssentials =
      String(el.dataset.hasEssentials || "").toLowerCase() === "true";

    if (!hasEssentials) {
      clearMountIfNoRecommendation(el);
      hideProductEssentialsUI(el);
      return;
    }

    const rec = await fetchEssentialForCartItem(itemInput);
    if (!rec?.input) {
      clearMountIfNoRecommendation(el);
      return;
    }

    const resolved = await resolveVariantFromInput(rec.input);
    if (!resolved?.variantId || !resolved?.product) {
      clearMountIfNoRecommendation(el);
      return;
    }

    el.dataset.ceEssentialHandle = resolved.handle || "";

    const inCart =
      isOptimisticInCart(resolved.variantId) ||
      (cart.items || []).some((li) => Number(li.variant_id) === Number(resolved.variantId));

    const p = resolved.product;
    const img = p.featured_image || (p.images && p.images[0]) || "";
    const price = p?.variants?.[0]?.price ?? null;

    patchMountUI(el, {
      title: String(title),
      variantId: Number(resolved.variantId),
      qty: Number(rec.qty || 1),
      inCart,
      name: String(p.title || resolved.handle || ""),
      price: price !== null ? formatMoneyFromCentsOrDollars(price) : "",
      img: String(img || ""),
    });
  } finally {
    el.dataset.ceRendering = "0";
  }
}

async function renderProductMount(el, cart) {
  if (!el || !el.isConnected) return;
  if (el.dataset.ceRendering === "1") return;
  if (isMountBusy(el)) return;

  el.dataset.ceRendering = "1";

  try {
    const itemInputRaw = el.dataset.productHandle || "";
    const itemInput = normalizeHandle(itemInputRaw);
    const title = el.dataset.productTitle || "this item";
    
    const hasEssentials =
      String(el.dataset.hasEssentials || "").toLowerCase() === "true";

    if (!hasEssentials) {
      clearMountIfNoRecommendation(el);
      hideProductEssentialsUI(el);
      return;
    }

    const rec = await fetchEssentialForCartItem(itemInput);

    if (!rec?.input) {
      clearMountIfNoRecommendation(el);
      hideProductEssentialsUI(el);
      return;
    }

    const resolved = await resolveVariantFromInput(rec.input);

    if (!resolved?.variantId || !resolved?.product) {
      clearMountIfNoRecommendation(el);
      hideProductEssentialsUI(el);
      return;
    }

    el.dataset.ceEssentialHandle = resolved.handle || "";

    const inCart =
      isOptimisticInCart(resolved.variantId) ||
      (cart.items || []).some((li) => Number(li.variant_id) === Number(resolved.variantId));

    const p = resolved.product;
    const img = p.featured_image || (p.images && p.images[0]) || "";
    const price = p?.variants?.[0]?.price ?? null;

    patchMountUI(el, {
      title: String(title),
      variantId: Number(resolved.variantId),
      qty: Number(rec.qty || 1),
      inCart,
      name: String(p.title || resolved.handle || ""),
      price: price !== null ? formatMoneyFromCentsOrDollars(price) : "",
      img: String(img || ""),
    });

    showProductEssentialsUI(el);

    const root = getProductEssentialsRoot(el);
    if (root) bindAddBothButton(root);
    else bindAddBothButton();
  } finally {
    el.dataset.ceRendering = "0";
  }
}

/* ------------------------------ render all ------------------------------ */

async function renderAllEssentialsMounts() {
  clearExpiredOptimistic();

  const drawerMounts = Array.from(document.querySelectorAll("[data-cart-essentials-drawer]"));
  const productMounts = Array.from(document.querySelectorAll("[data-cart-essentials-product]"));

  if (!drawerMounts.length && !productMounts.length) return;

  const cart = await getCartCached(false);

  await Promise.all([
    ...drawerMounts.map((el) => {
      if (!el.isConnected) return Promise.resolve();
      if (isMountBusy(el)) return Promise.resolve();
      return renderDrawerMount(el, cart);
    }),
    ...productMounts.map((el) => {
      if (!el.isConnected) return Promise.resolve();
      if (isMountBusy(el)) return Promise.resolve();
      return renderProductMount(el, cart);
    }),
  ]);
}

/* ------------------------------ boot ------------------------------ */

document.addEventListener("DOMContentLoaded", () => {
  const productRoots = document.querySelectorAll("[data-cart-essentials-root]");
  productRoots.forEach((root) => {
    root.hidden = true;
    root.style.display = "none";
  });

  ensureReminderModal();

  scheduleRenderAllEssentialsMounts();
  bindProductFormReminderIntercept();

  const obs = new MutationObserver(() => {
    scheduleRenderAllEssentialsMounts();
    bindProductFormReminderIntercept();
  });

  obs.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  document.addEventListener("cart:refresh", forceFreshEssentialsRender);
  document.addEventListener("cart:updated", forceFreshEssentialsRender);
  document.addEventListener("cart:change", forceFreshEssentialsRender);
  document.addEventListener("cart:changed", forceFreshEssentialsRender);

  document.addEventListener(
    "click",
    (e) => {
      const target = e.target?.closest?.(
        'button[name="minus"], button[name="plus"], cart-remove-button, [data-cart-remove], .cart-remove, .quantity__button'
      );
      if (!target) return;

      window.setTimeout(() => {
        forceFreshEssentialsRender();
      }, 150);
    },
    true
  );

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      if (_essentialsReminderModal && !_essentialsReminderModal.hidden && !_reminderModalBusy) {
        closeReminderModal();
      }
    }
  });
});

/* ------------------------------ public api ------------------------------ */

window.PoCEssentials = window.PoCEssentials || {};
window.PoCEssentials.renderAllEssentialsMounts = renderAllEssentialsMounts;
window.PoCEssentials.renderAllDrawerMounts = renderAllEssentialsMounts;
window.PoCEssentials.fetchEssentialForCartItem = fetchEssentialForCartItem;
window.PoCEssentials.resolveVariantFromInput = resolveVariantFromInput;
window.PoCEssentials.addVariantToCart = addVariantToCart;
window.PoCEssentials.addItemsToCart = addItemsToCart;
window.PoCEssentials.getCartCached = getCartCached;
window.PoCEssentials.refreshCartDrawerAndBubble = refreshCartDrawerAndBubble;
window.PoCEssentials.refreshDrawerViaSectionId = refreshDrawerViaSectionId;
window.PoCEssentials.refreshDrawerViaSectionsParam = refreshDrawerViaSectionsParam;
window.PoCEssentials.refreshCartPageSections = refreshCartPageSections;
window.PoCEssentials.forceFreshEssentialsRender = forceFreshEssentialsRender;
window.PoCEssentials.closeReminderModal = closeReminderModal;
window.PoCEssentials.trackFeatureClick = trackFeatureClick;